import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, Mail } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const INFO_EMAIL = "info@gortonsuperstore.uk";
const DELIVERY_EMAIL = "delivery@gortonsuperstore.uk";

const FAQS = [
  {
    q: "How do I place an order?",
    a: "Browse the categories, add items to your basket, and go to checkout. You can choose Collection (7 days a week) or Home Delivery (Saturdays only, Manchester M postcodes). Pay online with card or select 'Pay at Pickup' for collection orders.",
  },
  {
    q: "Do you deliver?",
    a: "Yes — we deliver across Manchester (M postcodes only) every Saturday between 9am and 6pm. Minimum order £50. Delivery fees are £7.00 for Zone 1, £7.50 for Zone 2, and £8.00 for Zone 3 depending on your postcode.",
  },
  {
    q: "What are your Click & Collect hours?",
    a: "Collection is available 7 days a week from our store at 578 Hyde Road, Manchester M18 7EE. Choose your slot at checkout — most orders can be ready within 2 hours.",
  },
  {
    q: "How do I pay?",
    a: "We accept card payments online through Stripe (Visa, Mastercard, American Express) and cash or card at pickup. Home delivery orders must be paid by card at the time of ordering.",
  },
  {
    q: "Can I amend or cancel my order?",
    a: `Please contact us as soon as possible at ${DELIVERY_EMAIL} or by phone. We can usually amend or cancel orders that have not yet been picked or dispatched. Once your order has been packed we may not be able to make changes.`,
  },
  {
    q: "What if an item is out of stock?",
    a: "If a product becomes unavailable after you order, we will attempt to deliver a suitable substitute unless you have asked us not to, in which case we will refund that item.",
  },
  {
    q: "What is your refund and returns policy?",
    a: "Non-perishable items: 14 days from delivery. Perishable items (plantains, bread, etc.): 24 hours from delivery. Please see our full Refund and Returns page for details.",
  },
  {
    q: "Do you offer bulk / wholesale pricing?",
    a: `Yes — we carry a wide range of wholesale boxes and 5kg / 10kg / 20kg pack sizes. For larger orders or trade enquiries please email buyers@gortonsuperstore.uk.`,
  },
  {
    q: "Are your products halal?",
    a: "Yes — our Halal Meat & Poultry category is fully halal certified. Many of our other groceries are also suitable; check the individual product page for details.",
  },
  {
    q: "How do I contact customer service?",
    a: `For orders and deliveries: ${DELIVERY_EMAIL}. For general enquiries: ${INFO_EMAIL}. You can also reach us by phone on 07404 133860 or WhatsApp on the same number.`,
  },
];

function FAQItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-slate-200 last:border-0"
      data-testid={`faq-item-${index}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left group"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-medium text-slate-900 group-hover:text-emerald-700 transition-colors">
          {item.q}
        </span>
        <ChevronDown
          className={`h-5 w-5 text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className="pb-5 pr-8 text-slate-700 leading-relaxed"
          data-testid={`faq-answer-${index}`}
        >
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function FaqsPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="faqs-page">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <Link
          to="/"
          className="inline-flex items-center text-slate-600 hover:text-emerald-600 mb-8 text-sm"
          data-testid="faqs-back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
          Frequently Asked Questions
        </h1>
        <p className="text-slate-600 mb-10">
          Everything you need to know about ordering from Gorton Superstore.
        </p>

        <div className="bg-white border border-slate-200 rounded-2xl px-6">
          {FAQS.map((item, i) => (
            <FAQItem key={item.q} item={item} index={i} />
          ))}
        </div>

        <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mt-10">
          <h2 className="text-xl font-semibold text-emerald-900 mb-2 flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Still have a question?
          </h2>
          <p className="text-emerald-900 mb-0">
            Send us an email at{" "}
            <a
              href={`mailto:${INFO_EMAIL}`}
              className="font-semibold text-emerald-700 hover:underline"
              data-testid="faqs-support-email"
            >
              {INFO_EMAIL}
            </a>{" "}
            and we&apos;ll get back to you as soon as we can.
          </p>
        </section>
      </div>

      <Footer />
    </div>
  );
}
